/**
 * JS and resource error telemetry: the `client_error` event.
 *
 * The Meta load drop-off analysis (ops/meta-load-drop-1021) could not tell a technical
 * failure from a player leaving, because nothing in the schema reported an error: 19 of 111
 * non-starters sat on the page for 5–30 s and never reached `game_ready`, and "JS errors:
 * not measurable" was the answer for all of them. This fixes exactly that gap.
 *
 * What is reported: `window.onerror` (a thrown error), `unhandledrejection` (a rejected
 * promise nobody handled) and a failed element load (script, module, stylesheet, image,
 * iframe) — the last one only through a capturing listener, because resource errors do not
 * bubble. The portal installs it for its own window and, on a play page, for the game
 * iframe's window too (same origin), so a game that fails to boot is reported as `game`
 * instead of silently never saying `ready`.
 *
 * What is NOT reported: a stack trace. The event carries the error's name, a scrubbed
 * message (src/lib/error-text.js), the file's origin + path with the query string removed,
 * and the line/column. Nothing else from the page.
 *
 * Volume is bounded: identical failures are reported once (a render loop can throw 60 times
 * a second) and a page view sends at most MAX_ERRORS. Consent is the tracker's own gate.
 */
import { MESSAGE_MAX, errorFileText, redactErrorText } from "./error-text.js";

/** Which window the failure happened in. */
export const ERROR_SOURCES = Object.freeze(["portal", "game"]);
/** How it failed. */
export const ERROR_KINDS = Object.freeze(["js", "promise", "resource"]);
/** Most client_error events one page view (or one game frame) sends. */
export const MAX_ERRORS = 5;
/** Elements whose failed load is worth an event (a broken asset, not a missing favicon). */
const RESOURCE_TAGS = new Set(["script", "link", "img", "iframe", "source", "audio", "video"]);

const LINE = 10_000_000;
const clampLine = (value) => {
  const n = Math.round(Number(value));
  return Number.isFinite(n) && n > 0 && n <= LINE ? n : null;
};

/**
 * The `client_error` props for one failure, or null when there is nothing to report
 * (no message and no file: an opaque cross-origin "Script error." with no detail).
 */
export function errorProps({ kind, source = "portal", name, message, file, line, col, page, extra } = {}) {
  if (!ERROR_KINDS.includes(kind) || !ERROR_SOURCES.includes(source)) return null;
  const text = redactErrorText(message, MESSAGE_MAX);
  const where = errorFileText(file);
  if (!text && !where) return null;
  const props = {
    error_kind: kind,
    error_source: source,
    error_message: text || "unknown error",
    // An invite's /join/<code> is a bearer secret (same rule as analytics.js safePath).
    page: String(page || "/").replace(/^\/join\/[^?#]*/, "/join/_").slice(0, 500),
  };
  const errorName = redactErrorText(name, 60);
  if (errorName) props.error_name = errorName;
  if (where) props.error_file = where;
  const lineNo = clampLine(line);
  if (lineNo !== null) props.error_line = lineNo;
  const colNo = clampLine(col);
  if (colNo !== null) props.error_col = colNo;
  return { ...(extra && typeof extra === "object" ? extra : {}), ...props };
}

/**
 * Errors thrown by the code Meta's in-app browsers inject into every page, not by ours.
 *
 * Facebook and Instagram on Android inject a navigation-performance logger that calls
 * `postMessage` / `postEvent` on a Java bridge; when the WebView is torn down (the player
 * closes the in-app browser or navigates) the bridge is gone and it throws "Error invoking
 * postMessage: Java object is gone" from a script with no URL. On iOS the same apps inject a
 * script that reads `window.webkit.messageHandlers`, which throws when the handler is not
 * there; WebKit reports it against the page document itself. No Barumba code touches either
 * bridge (test/fb-webview.test.mjs pins that), so these say nothing about our page: Oct 27
 * brain-1027 had 8 such rows and no first-party failure behind any of them. The iOS apps'
 * inner-frame shim also reads `window._META.IABInnerFrame` before Meta has defined it
 * ("undefined is not an object (evaluating 'window._META.IABInnerFrame')", Oct 29 noon
 * /brain-teasers rows); no Barumba code reads `_META` either.
 *
 * Matched only when the error comes from no file, a non-web URL (`app://…`), or the page
 * document (an injected inline script); the same text from one of our .js files still
 * reports.
 */
const IAB_BRIDGE_MESSAGE = /Error invoking post(?:Message|Event): Java (?:object is gone|exception was raised during method invocation)|webkit\.messageHandlers|window\._META\b|IABInnerFrame/;

export function isInAppBridgeNoise({ message, file, pageUrl } = {}) {
  if (!IAB_BRIDGE_MESSAGE.test(String(message || ""))) return false;
  const raw = String(file || "").trim();
  if (!raw || raw === "undefined") return true;
  let url;
  try { url = new URL(raw); } catch { return true; }
  if (url.protocol !== "http:" && url.protocol !== "https:") return true;
  try {
    const page = new URL(String(pageUrl || ""));
    return url.origin === page.origin && url.pathname === page.pathname;
  } catch {
    return false;
  }
}

/**
 * The error_name for a failed element load. A `<link rel=preload|modulepreload>` that fails
 * is only a hint (the real request may still succeed, or the asset may never be used), so it
 * is named "preload" to keep it apart from a stylesheet or icon `link` that really broke. It
 * is still reported, never dropped.
 */
function resourceName(tag, target) {
  if (tag !== "link") return tag;
  let rel = "";
  try { rel = String(target.rel || target.getAttribute?.("rel") || "").toLowerCase(); } catch { /* keep "link" */ }
  return /(?:^|\s)(?:module)?preload(?:\s|$)/.test(rel) ? "preload" : tag;
}

/** One failure per signature: a loop that throws every frame costs one event, not thousands. */
const signature = (props) => [props.error_kind, props.error_name, props.error_message, props.error_file, props.error_line].join("|");

/**
 * Listen for failures on `win` and send one `client_error` each. Returns an uninstall
 * function.
 *
 * `extra` is called for every event and may add the page's own context (game_id, load_id);
 * it never overrides a scrubbed field, because the props are merged in that order. A
 * throwing listener is impossible to observe from here, so everything inside is defensive:
 * telemetry must never be the reason a page breaks.
 */
export function installErrorTracking({
  win = globalThis.window, doc = win?.document, tracker, source = "portal",
  page = () => win?.location?.pathname || "/", extra = () => ({}), max = MAX_ERRORS,
} = {}) {
  if (!win?.addEventListener || !tracker?.track) return () => {};
  const seen = new Set();
  let sent = 0;

  function report(detail) {
    if (sent >= max) return null;
    try {
      if (detail.kind !== "resource" && isInAppBridgeNoise({ message: detail.message, file: detail.file, pageUrl: win?.location?.href })) return null;
    } catch {
      /* never let the filter be the failure */
    }
    let props;
    try {
      props = errorProps({ ...detail, source, page: page(), extra: extra() });
    } catch {
      return null;
    }
    if (!props) return null;
    const key = signature(props);
    if (seen.has(key)) return null;
    seen.add(key);
    sent += 1;
    try {
      // Keepalive flush at once: the page that just threw may be seconds from being closed,
      // and the 5 s batch timer would take the event with it.
      if (tracker.track("client_error", props)) tracker.flush();
    } catch {
      /* analytics never re-throws into a page that is already failing */
    }
    return props;
  }

  const onError = (event) => {
    const target = event?.target;
    const tag = typeof target?.tagName === "string" ? target.tagName.toLowerCase() : "";
    // A failed element load: no message, no line — the URL is the whole story.
    if (target && target !== win && RESOURCE_TAGS.has(tag)) {
      report({ kind: "resource", name: resourceName(tag, target), message: `${tag} failed to load`, file: target.src || target.href || target.currentSrc });
      return;
    }
    report({
      kind: "js",
      name: event?.error?.name,
      message: event?.message || event?.error?.message,
      file: event?.filename,
      line: event?.lineno,
      col: event?.colno,
    });
  };
  const onRejection = (event) => {
    const reason = event?.reason;
    const message = typeof reason === "string" ? reason : reason?.message || (reason === undefined ? "" : String(reason));
    report({ kind: "promise", name: reason?.name, message, file: reason?.fileName, line: reason?.lineNumber });
  };

  // Capture: resource errors fire on the element and never bubble to the window.
  win.addEventListener("error", onError, true);
  win.addEventListener("unhandledrejection", onRejection);
  return () => {
    try {
      win.removeEventListener("error", onError, true);
      win.removeEventListener("unhandledrejection", onRejection);
    } catch {
      /* the window is already gone (a navigated iframe) */
    }
  };
}

/**
 * Watch a same-origin game iframe: its own window is replaced on every navigation, so the
 * listeners are re-installed on each `load`. A cross-origin frame (a future hosted game)
 * throws on `contentWindow` access and is skipped, with no effect on the page.
 *
 * The frame's first head script dispatches a synchronous, same-origin boot notification
 * before any styles or game modules load. Attach there so failed module loads and boot
 * exceptions are captured too; load remains a fallback for older game documents.
 */
export function trackIframeErrors({ iframe, tracker, extra = () => ({}), page = () => globalThis.location?.pathname || "/", win = globalThis.window }) {
  if (!iframe?.addEventListener || !tracker) return () => {};
  let off = () => {};
  let frameDoc = null;
  const onLoad = () => {
    let frameWin = null;
    try {
      frameWin = iframe.contentWindow;
      // Cross-origin: reading a property throws, so there is nothing to attach to.
      void frameWin?.location?.pathname;
    } catch {
      return;
    }
    if (!frameWin || frameDoc === frameWin.document) return;
    off();
    frameDoc = frameWin.document;
    off = installErrorTracking({ win: frameWin, doc: frameWin.document, tracker, source: "game", page, extra });
  };
  const onBoot = (event) => {
    if (event.detail === iframe.contentWindow) onLoad();
  };
  win?.addEventListener?.("barumba:frame-boot", onBoot);
  iframe.addEventListener("load", onLoad);
  return () => {
    off();
    iframe.removeEventListener("load", onLoad);
    win?.removeEventListener?.("barumba:frame-boot", onBoot);
  };
}
